/**
 * NewReflectionModal Component
 *
 * Multi-step modal for creating new reflections.
 * Supports three modes:
 * - Upload: Upload audio file via drag-and-drop
 * - Record: Record audio directly in the browser
 * - Chat: Start a chat-based journal reflection (no audio)
 * Step 2: Enter reflection details (title, description, context, therapy style)
 * Matches the magicpatterns design spec with clean modal layout.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import {
  Upload,
  Music,
  ArrowLeft,
  Loader2,
  Mic,
  MessageSquare,
} from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useAudioUpload } from "@/hooks/useAudioUpload";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useBrowserRecordingSession } from "@/hooks/useBrowserRecordingSession";
import { checkBrowserCompatibility } from "@/hooks/useBrowserRecording";
import { TherapyStyleSelector } from "@/components/TherapyStyleSelector";
import { ReflectionSpeakerSelector } from "./ReflectionSpeakerSelector";
import {
  resolvePartnerSelection,
  type SpeakerSelection,
} from "@/utils/speakerSelection";
import { UploadProgress } from "./UploadProgress";
import { BrowserRecordingStep } from "./BrowserRecordingStep";
import { createJournalReflection } from "@/services/reflections";
import { usePrimaryPartner } from "@/hooks/useConversationPartners";
import type { ExpectedSpeakers } from "@/types/reflection";

interface NewReflectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadComplete?: (reflectionId: string) => void;
  onUploadError?: (error: string) => void;
}

type Step = "source" | "recording" | "details" | "uploading";
type SourceMode = "upload" | "record" | "chat";

/**
 * Generates a default title for a new reflection using the current date/time.
 * Format: "January 22nd, 2026 at 10:34pm"
 */
function generateDefaultTitle(): string {
  return format(new Date(), "MMMM do, yyyy 'at' h:mmaaa");
}

export function NewReflectionModal({
  open,
  onOpenChange,
  onUploadComplete,
  onUploadError,
}: NewReflectionModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("source");
  const [sourceMode, setSourceMode] = useState<SourceMode>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [userContext, setUserContext] = useState("");
  const [selectedStyleId, setSelectedStyleId] = useState<string | null>(null);
  const [expectedSpeakers, setExpectedSpeakers] =
    useState<ExpectedSpeakers | null>(null);
  const [speakerSelection, setSpeakerSelection] = useState<SpeakerSelection>({
    type: "primary",
    partnerId: null,
  });
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const { data: currentUser } = useCurrentUser();
  const { data: primaryPartner } = usePrimaryPartner();

  // Track if we've initialized the title for this modal session
  const titleInitializedRef = useRef(false);

  // Set default title when modal opens
  useEffect(() => {
    if (open && !titleInitializedRef.current) {
      setTitle(generateDefaultTitle());
      titleInitializedRef.current = true;
    } else if (!open) {
      titleInitializedRef.current = false;
    }
  }, [open]);

  // Initialize speaker selection when primary partner is loaded
  useEffect(() => {
    if (
      primaryPartner &&
      speakerSelection.type === "primary" &&
      !speakerSelection.partnerId
    ) {
      setSpeakerSelection({ type: "primary", partnerId: primaryPartner.id });
    }
  }, [primaryPartner, speakerSelection]);

  // Browser recording
  const browserRecording = useBrowserRecordingSession();
  const [browserCompatibility] = useState(() => checkBrowserCompatibility());

  // Warn user before closing tab mid-recording
  useEffect(() => {
    const isRecording =
      browserRecording.recordingState.status === "recording" ||
      browserRecording.recordingState.status === "paused";
    if (!isRecording) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [browserRecording.recordingState.status]);

  const { state, upload, pause, resume, cancel, reset, validateFile } =
    useAudioUpload({
      onComplete: (reflection) => {
        onUploadComplete?.(reflection.id);
        handleClose();
      },
      onError: (error) => {
        onUploadError?.(error);
      },
    });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const selectedFile = acceptedFiles[0];
      if (selectedFile) {
        const error = validateFile(selectedFile);
        if (error) {
          onUploadError?.(error);
          return;
        }
        setFile(selectedFile);
      }
    },
    [validateFile, onUploadError]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "audio/m4a": [".m4a"],
      "audio/mp4": [".m4a"],
      "audio/x-m4a": [".m4a"],
    },
    maxFiles: 1,
    disabled: step === "uploading",
  });

  const handleClose = () => {
    // Cancel any active recording
    if (
      browserRecording.recordingState.status === "recording" ||
      browserRecording.recordingState.status === "paused"
    ) {
      browserRecording.cancelSession();
    }
    // Reset all state when closing
    setStep("source");
    setSourceMode("upload");
    setFile(null);
    setTitle("");
    setDescription("");
    setUserContext("");
    setSelectedStyleId(null);
    setExpectedSpeakers(null);
    setSpeakerSelection({ type: "primary", partnerId: null });
    setIsCreatingChat(false);
    reset();
    browserRecording.resetSession();
    onOpenChange(false);
  };

  const handleNext = () => {
    if (sourceMode === "upload" && file) {
      setStep("details");
    } else if (
      sourceMode === "record" &&
      browserRecording.recordingState.status === "completed"
    ) {
      setStep("details");
    }
  };

  const handleBack = () => {
    if (step === "details") {
      if (sourceMode === "record") {
        // Can't go back from details after recording - would lose the recording
        setStep("source");
        browserRecording.resetSession();
      } else {
        setStep("source");
      }
    } else if (step === "recording") {
      setStep("source");
    }
  };

  // Handle starting browser recording
  const handleStartRecording = async () => {
    try {
      await browserRecording.startSession();
      setStep("recording");
    } catch {
      // Error state is set in useBrowserRecordingSession; step stays on "source"
    }
  };

  // Handle stopping browser recording
  const handleStopRecording = async () => {
    try {
      const styleId = selectedStyleId || currentUser?.default_therapy_style_id;
      const response = await browserRecording.stopAndComplete({
        title: title || undefined,
        description: description || undefined,
        userContext: userContext || undefined,
        therapyStyleId: styleId || undefined,
        expectedSpeakers: expectedSpeakers || undefined,
      });
      onUploadComplete?.(response.reflection_id);
      handleClose();
    } catch (error) {
      onUploadError?.(
        error instanceof Error ? error.message : "Failed to complete recording"
      );
    }
  };

  // Handle canceling browser recording
  const handleCancelRecording = async () => {
    await browserRecording.cancelSession();
    setStep("source");
  };

  const handleUpload = async () => {
    if (!file) return;
    setStep("uploading");
    try {
      const styleId = selectedStyleId || currentUser?.default_therapy_style_id;
      const partnerSelection = resolvePartnerSelection(
        speakerSelection,
        primaryPartner?.id || null
      );
      await upload(
        file,
        title || undefined,
        description || undefined,
        userContext || undefined,
        styleId || undefined,
        expectedSpeakers || undefined,
        partnerSelection.conversation_partner_id,
        partnerSelection.is_with_primary_partner
      );
    } catch {
      // Error handled by hook
    }
  };

  const handleCancel = async () => {
    await cancel();
    handleClose();
  };

  const handleRetry = () => {
    if (file) {
      reset();
      setStep("details");
    }
  };

  // Handle creating a chat-based journal reflection
  const handleStartChat = async () => {
    setIsCreatingChat(true);
    try {
      const styleId = selectedStyleId || currentUser?.default_therapy_style_id;
      const reflection = await createJournalReflection({
        title: title || undefined,
        description: description || undefined,
        userContext: userContext || undefined,
        therapyStyleId: styleId || undefined,
      });

      // Close modal and navigate to the chat
      handleClose();
      navigate(`/reflections/${reflection.id}?tab=chat`);
    } catch (error) {
      onUploadError?.(
        error instanceof Error ? error.message : "Failed to create reflection"
      );
      setIsCreatingChat(false);
    }
  };

  const isUploading = state.status === "uploading" || state.status === "paused";
  const isError = state.status === "error";

  // Get modal title and description
  const getModalTitle = () => {
    switch (step) {
      case "source":
        return sourceMode === "chat" ? "New Reflection" : "New Recording";
      case "recording":
        return "Recording";
      case "details":
        return "Recording Details";
      case "uploading":
        return "Uploading";
      default:
        return "New Recording";
    }
  };

  const getModalDescription = () => {
    switch (step) {
      case "source":
        if (sourceMode === "upload") return "Upload your audio file";
        if (sourceMode === "record") return "Record your conversation";
        if (sourceMode === "chat") return "Start a chat-based reflection";
        return "";
      case "recording":
        return "Recording in progress";
      case "details":
        return "Add details about your recording";
      case "uploading":
        return "Please wait while your file uploads";
      default:
        return "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-[540px]"
        hideCloseButton={step === "uploading" || step === "recording"}
        onEscapeKeyDown={(e) => {
          if (step === "uploading" || step === "recording") {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (step === "uploading" || step === "recording") {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">{getModalTitle()}</DialogTitle>
          <DialogDescription>{getModalDescription()}</DialogDescription>
        </DialogHeader>

        {/* Step 1: Source Selection (Upload or Record) */}
        {step === "source" && (
          <div className="py-4">
            {/* Mode tabs */}
            <Tabs
              value={sourceMode}
              onValueChange={(v) => setSourceMode(v as SourceMode)}
              className="mb-4"
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="upload" className="gap-2">
                  <Upload className="h-4 w-4" />
                  Upload
                </TabsTrigger>
                <TabsTrigger
                  value="record"
                  className="gap-2"
                  disabled={!browserCompatibility.mediaRecorder}
                >
                  <Mic className="h-4 w-4" />
                  Record
                </TabsTrigger>
                <TabsTrigger value="chat" className="gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Chat
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Title input - shown for both modes */}
            <div className="space-y-1 mb-4">
              <Label htmlFor="source-title">Title</Label>
              <Input
                id="source-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your reflection a title"
              />
            </div>

            {/* Upload mode */}
            {sourceMode === "upload" && (
              <>
                <div
                  {...getRootProps()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer",
                    "transition-colors duration-200",
                    isDragActive
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/25 hover:border-primary/50"
                  )}
                >
                  <input {...getInputProps()} />
                  <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-base font-medium">Upload Audio File</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Drag and drop your M4A file here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mt-3">
                    Only M4A format supported
                  </p>
                </div>

                {/* Selected file preview */}
                {file && (
                  <div className="mt-4 flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Music className="h-8 w-8 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Record mode */}
            {sourceMode === "record" && (
              <BrowserRecordingStep
                recordingState={browserRecording.recordingState}
                sessionState={browserRecording.sessionState}
                compatibility={browserCompatibility}
                onStart={handleStartRecording}
                onStop={handleStopRecording}
                onCancel={handleCancelRecording}
              />
            )}

            {/* Chat mode - simplified form for journal reflections */}
            {sourceMode === "chat" && (
              <div className="space-y-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <MessageSquare className="h-10 w-10 mx-auto mb-2 text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Start a conversation with your AI therapist. Share
                    what&apos;s on your mind and receive thoughtful,
                    personalized feedback.
                  </p>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="chat-description">
                    Description (optional)
                  </Label>
                  <Textarea
                    id="chat-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What would you like to reflect on today?"
                    rows={2}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="chat-context">
                    Context (optional)
                    <span className="text-xs text-muted-foreground ml-2">
                      Help your therapist understand your situation
                    </span>
                  </Label>
                  <Textarea
                    id="chat-context"
                    value={userContext}
                    onChange={(e) => setUserContext(e.target.value)}
                    placeholder="e.g., 'I've been feeling stressed about work lately...'"
                    rows={2}
                    maxLength={2000}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="chat-therapy-style">Therapy Style</Label>
                  <TherapyStyleSelector
                    value={selectedStyleId}
                    onChange={setSelectedStyleId}
                    variant="compact"
                    showUseDefault={true}
                  />
                  {currentUser?.default_therapy_style && !selectedStyleId && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Will use your default:{" "}
                      {currentUser.default_therapy_style.name}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Recording in Progress */}
        {step === "recording" && (
          <BrowserRecordingStep
            recordingState={browserRecording.recordingState}
            sessionState={browserRecording.sessionState}
            compatibility={browserCompatibility}
            onStart={handleStartRecording}
            onStop={handleStopRecording}
            onCancel={handleCancelRecording}
          />
        )}

        {/* Step 3: Details (for file upload mode only) */}
        {step === "details" && sourceMode === "upload" && file && (
          <div className="py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Selected file info */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Music className="h-8 w-8 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a description"
                rows={2}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="userContext">
                Context (optional)
                <span className="text-xs text-muted-foreground ml-2">
                  What should I know about this recording?
                </span>
              </Label>
              <Textarea
                id="userContext"
                value={userContext}
                onChange={(e) => setUserContext(e.target.value)}
                placeholder="e.g., 'In this recording, we discuss finances. I notice I get defensive when they bring up spending...'"
                rows={2}
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground">
                {userContext.length}/2000 characters
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="therapyStyle">Therapy Style</Label>
              <TherapyStyleSelector
                value={selectedStyleId}
                onChange={setSelectedStyleId}
                variant="compact"
                showUseDefault={true}
              />
              {currentUser?.default_therapy_style && !selectedStyleId && (
                <p className="text-xs text-muted-foreground mt-1">
                  Will use your default:{" "}
                  {currentUser.default_therapy_style.name}
                </p>
              )}
            </div>

            {/* Speaker Selection */}
            <div className="border-t pt-4">
              <ReflectionSpeakerSelector
                value={speakerSelection}
                onChange={setSpeakerSelection}
              />
            </div>
          </div>
        )}

        {/* Step 3: Uploading */}
        {step === "uploading" && file && (
          <div className="py-4">
            {(isUploading || isError) && (
              <UploadProgress
                state={state}
                filename={file.name}
                onPause={pause}
                onResume={resume}
                onCancel={handleCancel}
                onRetry={handleRetry}
              />
            )}
            {state.status === "completed" && (
              <div className="text-center p-6">
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 mb-3">
                  <Music className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Upload Complete!
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  Your reflection is being processed
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="sm:justify-between">
          {step === "source" && sourceMode === "upload" && (
            <>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleNext} disabled={!file}>
                Next
              </Button>
            </>
          )}
          {step === "source" && sourceMode === "record" && (
            <>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              {/* Start recording button is inside BrowserRecordingStep */}
              <div />
            </>
          )}
          {step === "source" && sourceMode === "chat" && (
            <>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleStartChat} disabled={isCreatingChat}>
                {isCreatingChat ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Start Chat
                  </>
                )}
              </Button>
            </>
          )}
          {step === "recording" && (
            <div className="w-full">
              {/* Recording controls are inside BrowserRecordingStep */}
            </div>
          )}
          {step === "details" && sourceMode === "upload" && (
            <>
              <Button variant="ghost" onClick={handleBack} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleUpload}>Upload Reflection</Button>
            </>
          )}
          {step === "uploading" && !isError && state.status !== "completed" && (
            <div className="w-full flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
